import React, { useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useTheme } from "@mui/material/styles";
import { Link, useLocation } from "react-router-dom";

function Header() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [developerAnchorEl, setDeveloperAnchorEl] = useState(null);
  const [aboutAnchorEl, setAboutAnchorEl] = useState(null);
  const theme = useTheme();
  const location = useLocation();

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  const handleDeveloperOpen = (event) => {
    setDeveloperAnchorEl(event.currentTarget);
  };

  const handleAboutOpen = (event) => {
    setAboutAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setDeveloperAnchorEl(null);
    setAboutAnchorEl(null);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const navItems = [
    { href: "/", label: "Home" },
    { href: "/developer", label: "Developer" },
    { href: "/download", label: "Download" },
    { href: "/price", label: "Price" },
  ];

  const drawerContent = (
    <Box
      sx={{ width: 280 }}
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <Box sx={{ p: 2, borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: theme.palette.primary.main,
          }}
        >
          mhome.ai
        </Typography>
      </Box>
      <List>
        {navItems.map((item) => (
          <ListItem
            key={item.href}
            button
            component={Link}
            to={item.href}
            sx={{
              color: isActive(item.href)
                ? theme.palette.primary.main
                : "inherit",
              backgroundColor: isActive(item.href)
                ? "rgba(33, 150, 243, 0.1)"
                : "transparent",
              "&:hover": {
                backgroundColor: "rgba(33, 150, 243, 0.05)",
              },
            }}
          >
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
        <ListItem
          button
          component="a"
          href="https://app.mhome.ai"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: theme.palette.primary.main,
            "&:hover": {
              backgroundColor: "rgba(33, 150, 243, 0.05)",
            },
          }}
        >
          <ListItemText primary="App" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: theme.palette.background.header,
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)", // Safari support
        border: `1px solid ${theme.palette.border.light}`,
        borderTop: "none",
        borderLeft: "none",
        borderRight: "none",
        boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)",
          pointerEvents: "none",
        },
      }}
      elevation={0}
    >
      <Toolbar sx={{ height: 64 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Link
            to="/"
            style={{
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <img
              src="/logo512.png"
              alt="mhome.ai logo"
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                fontSize: "1.25rem",
                transition: `opacity ${theme.transitions.duration.short}ms ease`,
                "&:hover": {
                  opacity: 0.8,
                },
              }}
            >
              mhome.ai
            </Typography>
          </Link>
        </Box>

        {/* Desktop Navigation */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 1,
          }}
        >
          {navItems.map((item) => (
            <Button
              key={item.href}
              component={Link}
              to={item.href}
              sx={{
                color: isActive(item.href)
                  ? theme.palette.primary.main
                  : theme.palette.text.secondary,
                textTransform: "none",
                fontWeight: isActive(item.href) ? 600 : 500,
                px: 2,
                py: 1,
                borderRadius: 2,
                transition: `all ${theme.transitions.duration.short}ms ease`,
                "&:hover": {
                  backgroundColor: theme.palette.background.glassStrong,
                  color: theme.palette.primary.main,
                },
              }}
            >
              {item.label}
            </Button>
          ))}
          <Button
            variant="contained"
            component="a"
            href="https://app.mhome.ai"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              background: theme.palette.brand.gradient,
              color: theme.palette.text.white,
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              py: 1,
              ml: 2,
              borderRadius: 2,
              boxShadow: theme.shadows[6],
              transition: `all ${theme.transitions.duration.short}ms ease`,
              "&:hover": {
                background: theme.palette.brand.gradientHover,
                transform: "translateY(-1px)",
                boxShadow: theme.shadows[7],
              },
            }}
          >
            Get Started
          </Button>
        </Box>

        {/* Mobile menu button */}
        <IconButton
          sx={{
            color: theme.palette.text.primary,
            display: { xs: "block", md: "none" },
          }}
          aria-label="open drawer"
          edge="start"
          onClick={toggleDrawer(true)}
        >
          <MenuIcon />
        </IconButton>
      </Toolbar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.drawer,
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)", // Safari support
            color: theme.palette.text.primary,
            borderRight: `1px solid ${theme.palette.border.light}`,
            boxShadow: "4px 0 12px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </AppBar>
  );
}

export default Header;
